import React, { useState } from 'react';
import { Info, Upload, Delete as DeleteIcon, Music, Loading } from '@icon-park/react';
import { uploadFile, FILE_TYPES } from '../../api/upload';
import { formatFileSize } from '../../utils/fileUtils';
import './styles/AudioUploader.css';

function AudioUploader({ data, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      console.log(`开始上传音频: ${file.name}, 大小: ${formatFileSize(file.size)}`);

      // 上传音频到OSS/CDN
      const audioUrl = await uploadFile(file, {
        fileType: FILE_TYPES.AUDIO,
        folder: 'labels/audios',
        onProgress: (percent) => {
          setUploadProgress(percent);
          console.log(`音频上传进度: ${percent}%`);
        }
      });

      // 更新数据（只保存URL）
      onUpdate({ 
        ...data, 
        audio: { 
          url: audioUrl,  // ✅ CDN URL
          name: file.name,
          size: file.size,
          sizeFormatted: formatFileSize(file.size)
        } 
      });

      console.log('音频上传成功:', audioUrl);
    } catch (err) {
      console.error('音频上传失败:', err);
      setError(err.message || '音频上传失败，请重试');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteAudio = () => {
    onUpdate({ ...data, audio: null });
  };

  return (
    <div className="audio-uploader-container">
      <div className="uploader-header">
        <span className="uploader-title">音频样式</span>
        <span className="info-icon"><Info theme="outline" size={14} fill="#999" strokeWidth={3} /></span>
      </div>
      
      <h3 className="section-title">音频添加</h3>
      
      {/* 错误提示 */}
      {error && (
        <div className="upload-error">
          ⚠️ {error}
        </div>
      )}

      {!data.audio ? (
        <div className="upload-area-audio">
          <input
            type="file"
            id="audio-upload"
            accept="audio/*"
            onChange={handleAudioUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <label 
            htmlFor="audio-upload" 
            className={`upload-box-audio ${uploading ? 'uploading' : ''}`}
          >
            {uploading ? (
              <>
                <div className="upload-icon-audio">
                <Loading theme="outline" size={20} fill="#2563EB" strokeWidth={3} />
                </div>
                <div className="upload-text-audio">上传中... {uploadProgress}%</div>
                <div className="upload-progress-bar">
                  <div 
                    className="upload-progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="upload-icon-audio">
                  <Music theme="outline" size={20} fill="#333" strokeWidth={3} />
                </div>
                <div className="upload-text-audio">点击上传音频文件</div>
                <div className="upload-hint">支持MP3、WAV、AAC，最大20MB</div>
              </>
            )}
          </label>
        </div>
      ) : (
        <div className="audio-preview">
          <audio 
            src={data.audio.url} 
            controls={(data.audioOptions ? data.audioOptions.controls !== false : true)}
            autoPlay={!!(data.audioOptions && data.audioOptions.autoplay)}
            loop={!!(data.audioOptions && data.audioOptions.loop)}
            className="audio-player"
            style={{
              borderRadius: data.playerStyle && data.playerStyle.radius ? data.playerStyle.radius : 0,
              border: data.playerStyle && data.playerStyle.borderWidth ? `${data.playerStyle.borderWidth}px solid ${data.playerStyle.borderColor || '#e8eaed'}` : 'none'
            }}
          />
          <div className="audio-info">
            <div className="audio-details">
              <span className="audio-name">🎵 {data.audio.name}</span>
              {data.audio.sizeFormatted && (
                <span className="audio-size">{data.audio.sizeFormatted}</span>
              )}
            </div>
            <button className="delete-audio-btn" onClick={handleDeleteAudio}>
              <DeleteIcon theme="outline" size={14} fill="#fff" strokeWidth={3} />
              删除
            </button>
          </div>
        </div>
      )}

      {/* 播放器样式与选项 */}
      <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label>圆角(px)</label>
        <input type="number" min="0" max="48" value={(data.playerStyle && data.playerStyle.radius) || 0} onChange={(e) => onUpdate({ ...data, playerStyle: { ...(data.playerStyle || {}), radius: Number(e.target.value) } })} />
        <label>边框宽(px)</label>
        <input type="number" min="0" max="12" value={(data.playerStyle && data.playerStyle.borderWidth) || 0} onChange={(e) => onUpdate({ ...data, playerStyle: { ...(data.playerStyle || {}), borderWidth: Number(e.target.value) } })} />
        <label>边框色</label>
        <input type="color" value={(data.playerStyle && data.playerStyle.borderColor) || '#e8eaed'} onChange={(e) => onUpdate({ ...data, playerStyle: { ...(data.playerStyle || {}), borderColor: e.target.value } })} />
        <label><input type="checkbox" checked={data.audioOptions ? data.audioOptions.controls !== false : true} onChange={(e) => onUpdate({ ...data, audioOptions: { ...(data.audioOptions || {}), controls: e.target.checked } })} /> 显示控件</label>
        <label><input type="checkbox" checked={!!(data.audioOptions && data.audioOptions.autoplay)} onChange={(e) => onUpdate({ ...data, audioOptions: { ...(data.audioOptions || {}), autoplay: e.target.checked } })} /> 自动播放</label>
        <label><input type="checkbox" checked={!!(data.audioOptions && data.audioOptions.loop)} onChange={(e) => onUpdate({ ...data, audioOptions: { ...(data.audioOptions || {}), loop: e.target.checked } })} /> 循环</label>
      </div>
    </div>
  );
}

export default AudioUploader;
